// Extrait de DataTab.js (handover bloc F1) — JSX, textes et appels API inchangés.
import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { FileText, Activity, Users, RefreshCw, ShieldCheck, BookOpen } from "lucide-react";
import { KPICard, QUALITY_LABELS } from "./shared";

const D2_COVERAGE_LABELS = { clinical_event: "Événements cliniques", lab_value: "Biologie gradée", oral_adherence: "Observance orale", pro_response: "Réponses PRO", appointment: "Rendez-vous", patient_lifecycle: "Cycle de vie du suivi" };
const D2_CONTEXT_LABELS = { ecogStatusPct: "ECOG renseigné", stageKnownPct: "Stade connu", biomarkersPct: "Biomarqueurs renseignés" };

function D2Metric({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-800">{value ?? "—"}</p>
    </div>
  );
}

function SponsorReportButtons() {
  const [busy, setBusy] = useState(null);
  const download = async (lang) => {
    setBusy(lang);
    try {
      const res = await api.get("/research/sponsor-report.pdf", { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `hana-sponsor-report-${lang}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Rapport sponsor indisponible (sel d'anonymisation ou volume).");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="sponsor-report-buttons">
      {["fr", "en"].map((lang) => (
        <button key={lang} type="button" onClick={() => download(lang)} disabled={busy !== null} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50" data-testid={`sponsor-report-download-${lang}`}>
          <FileText className="h-3.5 w-3.5" /> {busy === lang ? "Génération…" : `Rapport agrégé sponsor (${lang.toUpperCase()})`}
        </button>
      ))}
    </div>
  );
}

function D2QualityCard({ quality }) {
  const ps = quality.planSourceDistribution || {};
  const cad = quality.checkinCadence || {};
  const gaps = quality.consentGaps || {};
  return (
    <div className="dash-card" data-testid="rwe-quality-d2-card">
      <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
        <h4 className="font-medium text-slate-800">Qualité du dataset typé (lot D2)</h4>
        <SponsorReportButtons />
      </div>
      <p className="mb-4 text-xs text-slate-500">Agrégats sur les patients actifs du périmètre ; aucun identifiant. Le rapport sponsor est construit sur le dataset typé, consentement RESEARCH_PHARMA, k = 5 (lot D4). Référence : docs/DATA_DICTIONARY_RWE.md §6.</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <D2Metric label="Plan structuré" value={ps.structured} testid="rwe-d2-plan-structured" />
        <D2Metric label="Protocole inféré (legacy)" value={ps.legacy_inferred} testid="rwe-d2-plan-legacy-inferred" />
        <D2Metric label="Sans plan ni protocole (legacy)" value={ps.legacy} testid="rwe-d2-plan-legacy" />
        <D2Metric label="Consentants pharma sans événement RWE (90 j)" value={`${gaps.pharmaConsentedWithoutRweEvents90d ?? "—"} / ${gaps.pharmaConsentedActivePatients ?? "—"}`} testid="rwe-d2-consent-gap" />
        <D2Metric label="Patients avec check-in (30 j)" value={cad.patientsWithCheckin30dPct != null ? `${cad.patientsWithCheckin30dPct}%` : null} testid="rwe-d2-checkin-30d" />
        <D2Metric label="Check-ins par patient actif (30 j)" value={cad.checkinsPerActivePatient30d} testid="rwe-d2-checkin-rate" />
        <D2Metric label="Médiane jours entre check-ins" value={cad.medianDaysBetweenCheckins30d} testid="rwe-d2-checkin-median" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div data-testid="rwe-d2-coverage">
          <p className="mb-2 text-sm font-medium text-slate-700">Sources des nouveaux types (événements disponibles)</p>
          {Object.entries(quality.d1SourceCoverage || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-slate-100 py-1 text-sm" data-testid={`rwe-d2-coverage-${k}`}><span className="text-slate-600">{D2_COVERAGE_LABELS[k] || k}</span><span className="font-semibold text-slate-800">{v}</span></div>
          ))}
        </div>
        <div data-testid="rwe-d2-context">
          <p className="mb-2 text-sm font-medium text-slate-700">Contexte patient joint au dataset</p>
          {Object.entries(quality.patientContextCompleteness || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-slate-100 py-1 text-sm" data-testid={`rwe-d2-context-${k}`}><span className="text-slate-600">{D2_CONTEXT_LABELS[k] || k}</span><span className="font-semibold text-slate-800">{v}%</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function QualityGovernanceView({ filters }) {
  const [quality, setQuality] = useState(null);
  const [dictionary, setDictionary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchQuality = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) params.append("clinic_id", filters.clinicId);
      const [qualityRes, dictionaryRes] = await Promise.all([
        api.get(`/data/rwe-quality?${params.toString()}`),
        api.get("/data/sponsor-dictionary"),
      ]);
      setQuality(qualityRes.data);
      setDictionary(dictionaryRes.data);
    } catch (err) {
      toast.error("Erreur lors du chargement de la qualité RWE");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuality();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.clinicId]);

  if (loading && !quality) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="rwe-quality-loading">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  const volumes = quality?.volumes || {};
  const completeness = quality?.completeness || {};
  const consents = quality?.consents || {};
  const transmission = quality?.structuredTransmissions || {};

  return (
    <div className="space-y-6" data-testid="rwe-quality-governance-view">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Qualité RWE & gouvernance sponsor
          </h3>
          <p className="text-xs text-slate-500 mt-1" data-testid="rwe-quality-privacy-posture">
            Pseudonymisation forte avec techniques d’anonymisation — aucune revendication d’anonymisation complète.
          </p>
        </div>
        <button
          onClick={fetchQuality}
          disabled={loading}
          data-testid="rwe-quality-refresh-button"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={Users} label="Patients actifs" value={volumes.activePatients || 0} color="blue" />
        <KPICard icon={Activity} label="Événements RWE" value={volumes.rweEvents || 0} subValue={`${volumes.rweEventsWithClinicalContext || 0} avec contexte`} color="emerald" />
        <KPICard icon={FileText} label="Dataset recherche" value={volumes.researchDatasetRecords || 0} subValue={`${volumes.researchDatasetSnapshots || 0} snapshots`} color="teal" />
        <KPICard icon={ShieldCheck} label="Audits export" value={volumes.sponsorExportAudits || 0} color="amber" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="dash-card" data-testid="rwe-quality-completeness-card">
          <h4 className="font-medium text-slate-800 mb-4">Complétude des champs clés</h4>
          {Object.entries(completeness).map(([key, value]) => (
            <div key={key} className="mb-3" data-testid={`rwe-quality-completeness-${key}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">{QUALITY_LABELS[key] || key}</span>
                <span className="font-semibold text-slate-800">{value}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${Math.min(value || 0, 100)}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="dash-card" data-testid="rwe-quality-consents-card">
          <h4 className="font-medium text-slate-800 mb-4">Consentements recherche</h4>
          <div className="grid grid-cols-2 gap-3">
            <ConsentMetric title="Pharma accordés" value={consents.pharma?.granted || 0} testid="consent-pharma-granted" />
            <ConsentMetric title="Pharma refusés" value={consents.pharma?.refused || 0} testid="consent-pharma-refused" />
            <ConsentMetric title="Académique accordés" value={consents.academic?.granted || 0} testid="consent-academic-granted" />
            <ConsentMetric title="Académique refusés" value={consents.academic?.refused || 0} testid="consent-academic-refused" />
          </div>
          <p className="mt-4 text-xs text-slate-500" data-testid="rwe-quality-consent-note">
            Les exports sponsor restent conditionnés au dernier consentement RESEARCH_PHARMA accordé.
          </p>
        </div>
      </div>

      {quality?.d2Version && <D2QualityCard quality={quality} />}

      <div className="dash-card" data-testid="rwe-quality-transmissions-card">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-slate-800">Transmissions structurées</h4>
          <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium" data-testid="rwe-quality-transmission-coverage">
            Couverture RWE {transmission.rweCoveragePct || 0}%
          </span>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <MiniDistribution title="Priorités" data={transmission.byPriority} testid="transmission-priority-distribution" />
          <MiniDistribution title="Actions attendues" data={transmission.byActionRequired} testid="transmission-action-distribution" />
          <div data-testid="transmission-top-tags">
            <p className="text-sm font-medium text-slate-700 mb-2">Tags les plus fréquents</p>
            <div className="flex flex-wrap gap-2">
              {(transmission.topTags || []).length === 0 && <span className="text-xs text-slate-400">Aucune donnée</span>}
              {(transmission.topTags || []).map(([tag, count]) => (
                <span key={tag} className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">
                  {tag} · {count}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="dash-card" data-testid="sponsor-dictionary-card">
        <h4 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-indigo-600" />
          Dictionnaire sponsor
        </h4>
        <div className="grid md:grid-cols-2 gap-4">
          {(dictionary?.exportLevels || []).map(level => (
            <div key={level.level} className="border border-slate-100 rounded-lg p-3" data-testid={`sponsor-export-level-${level.level}`}>
              <p className="font-semibold text-slate-800 text-sm">{level.level}</p>
              <p className="text-xs text-slate-500 mt-1">{level.endpoint}</p>
              <p className="text-xs text-slate-600 mt-2">{String(level.externalSharingAllowed)}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-slate-500" data-testid="sponsor-dictionary-transmission-fields">
          Transmission RWE : {(dictionary?.structuredTransmissionRecord?.includedFields || []).join(", ")}
        </div>
      </div>
    </div>
  );
}


function ConsentMetric({ title, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-100 p-3" data-testid={testid}>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500">{title}</p>
    </div>
  );
}


function MiniDistribution({ title, data, testid }) {
  const entries = Object.entries(data || {});
  return (
    <div data-testid={testid}>
      <p className="text-sm font-medium text-slate-700 mb-2">{title}</p>
      <div className="space-y-2">
        {entries.length === 0 && <span className="text-xs text-slate-400">Aucune donnée</span>}
        {entries.map(([key, count]) => (
          <div key={key} className="flex items-center justify-between text-xs rounded-lg bg-slate-50 px-3 py-2">
            <span className="text-slate-600">{key}</span>
            <span className="font-semibold text-slate-800">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
